<script setup lang="ts">
import type {Contest as IContest} from "@/contest/interfaces";
import {Skeleton} from "@/core/components/ui/skeleton";
import Contest from "@/contest/components/Contest.vue";
import {Inbox} from "@lucide/vue";
import type {CarouselApi} from "@/core/components/ui/carousel";
import {Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious} from "@/core/components/ui/carousel";
import {computed, ref, watch} from "vue";
import ContestCreation from "@/contest/components/ContestCreation.vue";

const props = defineProps<{
    loading: boolean;
    contests: IContest[];
}>();

defineEmits(['on-refresh']);

const carouselApi = ref<CarouselApi>();
const currentSlide = ref(0);

function onCarouselInit(api: CarouselApi) {
    carouselApi.value = api;

    api?.on('select', () => {
        currentSlide.value = api.selectedScrollSnap();
    });
}

/**
 * Determine which contests should load their data based on the current slide.
 */
const contestsToLoad = computed(() => {
    const indices = new Set<number>();
    indices.add(currentSlide.value);
    return indices;
});

const goToSlide = (index: number) => {
    carouselApi.value?.scrollTo(index);
};

watch(() => props.contests.length, (newLength) => {
    if (currentSlide.value >= newLength) {
        currentSlide.value = 0;
        goToSlide(0);
    }
});
</script>

<template>
  <div class="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
    <div v-if="loading">
      <Skeleton class="w-full h-32 rounded-lg" />
    </div>
    <div v-else class="flex flex-col">
      <div v-if="!contests.length" class="text-center py-12 bg-gray-50 rounded-2xl p-6">
        <div class="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Inbox class="w-6 h-6 text-gray-400" />
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          Aucun challenge disponible
        </h3>
        <p class="text-gray-600">
          Les challenges de votre groupement seront bientôt disponibles.
        </p>
      </div>
      <div v-else>
        <div v-if="contests.length === 1">
          <Contest :contest="contests[0]" :should-load="true" />
        </div>
        <Carousel
          v-else
          :opts="{
            align: 'start',
            loop: true,
          }"
          class="w-full"
          @init-api="onCarouselInit"
        >
          <CarouselContent>
            <CarouselItem
              v-for="(contest, index) in contests"
              :key="contest.id"
            >
              <div class="m-2 p-4 rounded-lg shadow border border-primary">
                <Contest :contest="contest" :should-load="contestsToLoad.has(index)" @update:contest="$emit('on-refresh')" />
              </div>
            </CarouselItem>
          </CarouselContent>

          <CarouselPrevious class="hidden lg:flex lg:-left-24" />
          <CarouselNext class="hidden lg:flex lg:-right-24" />
        </Carousel>
        <div v-if="contests.length > 1" class="flex justify-center gap-2 mt-6">
          <button
            v-for="(contest, index) in contests"
            :key="`dot-${contest.id}`"
            type="button"
            :class="[
              'w-2 h-2 rounded-full transition-all duration-300',
              currentSlide === index
                ? 'gradient w-8'
                : 'bg-gray-300 hover:bg-gray-400'
            ]"
            @click="goToSlide(index)"
          />
        </div>
      </div>
    </div>
    <ContestCreation class="mt-4" @on-refresh="$emit('on-refresh')" />
  </div>
</template>

<style scoped>

</style>
